import React, { Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import {
  resolveHostnameRoutes,
  ROOT_TESTIMONIALS,
  SUBMIT_TESTIMONIALS,
  PATH_ROUTES,
  COMPONENT_TESTIMONIALS_PAGE,
  COMPONENT_SUBMIT_PAGE,
  COMPONENT_TESTIMONIALS_EMBED,
} from './hostnameRoutes';

const PublicEPK = React.lazy(() => import('./pages/PublicEPK'));
const AdminLogin = React.lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const AcceptInvite = React.lazy(() => import('./pages/AcceptInvite'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const TestimonialsPage = React.lazy(() => import('./pages/TestimonialsPage'));
const SubmitPage = React.lazy(() => import('./pages/SubmitPage'));
const TestimonialsEmbed = React.lazy(() => import('./pages/TestimonialsEmbed'));

// The one place a hostnameRoutes.js component key resolves to a real
// component. Keeps hostnameRoutes.js free of React imports while still
// making PATH_ROUTES the single source of truth for the path -> page
// mapping (see the .map() below) instead of duplicating each path as a
// hardcoded <Route> literal.
const PATH_ROUTE_COMPONENTS = {
  [COMPONENT_TESTIMONIALS_PAGE]: TestimonialsPage,
  [COMPONENT_SUBMIT_PAGE]: SubmitPage,
  [COMPONENT_TESTIMONIALS_EMBED]: TestimonialsEmbed,
};

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center bg-cream font-display text-oxblood uppercase tracking-widest text-2xl" data-testid="app-loading">
    Loading…
  </div>
);

const PreviewRoute = () => <PublicEPK isPreview />;

export default function App() {
  // ADR 0002: this one app serves multiple hostnames; pick what mounts at
  // `/` (and `/submit`) from the current hostname. The three testimonials
  // path routes are driven by PATH_ROUTES below and registered
  // unconditionally on every hostname.
  const { root, submit } = resolveHostnameRoutes(window.location.hostname);

  return (
    <ErrorBoundary>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={root === ROOT_TESTIMONIALS ? <TestimonialsPage /> : <PublicEPK />} />
          {submit === SUBMIT_TESTIMONIALS && <Route path="/submit" element={<SubmitPage />} />}
          {PATH_ROUTES.map(({ path, component }) => {
            const Component = PATH_ROUTE_COMPONENTS[component];
            return <Route key={path} path={path} element={<Component />} />;
          })}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/preview" element={<PreviewRoute />} />
          <Route path="/admin/accept-invite" element={<AcceptInvite />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
